import type { ApiResponse } from '@packages/shared-types';

import { apiClient } from '@/services/apiClient';

export interface FolderTreeNode {
  id: string;
  name: string;
  code: string;
  level: number;
  sortOrder: number;
  parentFolderId: string | null;
  children: FolderTreeNode[];
}

export interface Folder {
  id: string;
  tenantId: string;
  code: string;
  parentFolderId?: string | null;
  level: number;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateFolderPayload {
  name: string;
  parentFolderId?: string | null;
  code?: string;
  sortOrder?: number;
}

export interface UpdateFolderPayload {
  code?: string;
  name?: string;
  parentFolderId?: string | null;
  sortOrder?: number;
}

export interface DeleteFolderPayload {
  moveToFolderId?: string;
}

export async function getFoldersTree(): Promise<FolderTreeNode[]> {
  const response = await apiClient.get<ApiResponse<FolderTreeNode[]>>(
    '/api/folders/tree',
  );
  return response.data.data;
}

export async function getFolders(): Promise<Folder[]> {
  const response = await apiClient.get<ApiResponse<Folder[]>>('/api/folders');
  return response.data.data;
}

export async function getFolderById(id: string): Promise<Folder> {
  const response = await apiClient.get<ApiResponse<Folder>>(
    `/api/folders/${id}`,
  );
  return response.data.data;
}

export async function getNextFolderCode(): Promise<{ code: string }> {
  const response = await apiClient.get<ApiResponse<{ code: string }>>(
    '/api/folders/next-code',
  );
  return response.data.data;
}

export async function createFolder(
  payload: CreateFolderPayload,
): Promise<Folder> {
  const response = await apiClient.post<ApiResponse<Folder>>(
    '/api/folders',
    payload,
  );
  return response.data.data;
}

export async function updateFolder(
  id: string,
  payload: UpdateFolderPayload,
): Promise<Folder> {
  const response = await apiClient.put<ApiResponse<Folder>>(
    `/api/folders/${id}`,
    payload,
  );
  return response.data.data;
}

export async function deleteFolder(
  id: string,
  payload?: DeleteFolderPayload,
): Promise<void> {
  await apiClient.delete(`/api/folders/${id}`, { data: payload });
}
